import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { getUserAlerts } from '../../services/propertyAlertsService';
import { wishlistService } from '../../services/wishlistService';
import { getDigestPreferences, saveDigestPreferences } from '../../services/emailNotificationService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatPrice = (p) => {
  if (!p) return null;
  if (p >= 1000000) return `${(p / 1000000)?.toFixed(1)?.replace(/\.0$/, '')}M XOF`;
  if (p >= 1000) return `${(p / 1000)?.toFixed(0)}K XOF`;
  return `${p?.toLocaleString()} XOF`;
};

const FREQ_OPTIONS = [
  { value: 'weekly', label: 'Hebdomadaire', desc: 'Chaque lundi matin', icon: 'CalendarDays' },
  { value: 'monthly', label: 'Mensuel', desc: 'Le 1er du mois', icon: 'Calendar' },
  { value: 'daily', label: 'Quotidien', desc: 'Chaque matin à 8h', icon: 'Sun' },
];

const DAYS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const EVENT_CONFIG = {
  inquiry:  { label: 'Demande',        icon: 'MessageSquare', color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-100'   },
  viewing:  { label: 'Visite',         icon: 'Calendar',      color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-100' },
  saved:    { label: 'Sauvegardée',    icon: 'Heart',         color: 'text-rose-600',   bg: 'bg-rose-50',   border: 'border-rose-100'   },
  alert:    { label: 'Alerte',         icon: 'Bell',          color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-100'  },
};

const STATUS_LABELS = {
  new: 'Nouveau', pending: 'En attente', scheduled: 'Planifié',
  confirmed: 'Confirmé', completed: 'Terminé', cancelled: 'Annulé',
  rescheduled: 'Reprogrammé', active: 'Actif', inactive: 'Inactif',
};

// ─── PDF Generation ───────────────────────────────────────────────────────────

const generatePDFContent = (events, stats, period, userEmail) => {
  const now = new Date();
  const periodLabel = period === 'weekly' ? 'Hebdomadaire' : period === 'monthly' ? 'Mensuel' : 'Quotidien';
  const dateLabel = now?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const rows = events?.map(e => {
    const cfg = EVENT_CONFIG?.[e?.type];
    return `
      <tr style="border-bottom:1px solid #f0ebe0;">
        <td style="padding:10px 12px;font-size:12px;color:#6b5c3e;">${cfg?.label || e?.type}</td>
        <td style="padding:10px 12px;font-size:13px;font-weight:600;color:#1a1208;">${e?.title || '—'}</td>
        <td style="padding:10px 12px;font-size:12px;color:#6b5c3e;">${e?.subtitle || '—'}</td>
        <td style="padding:10px 12px;font-size:12px;color:#6b5c3e;">${STATUS_LABELS?.[e?.status] || e?.status || '—'}</td>
        <td style="padding:10px 12px;font-size:12px;color:#6b5c3e;">${formatDate(e?.date)}</td>
      </tr>
    `;
  })?.join('');

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8"/>
  <title>Digest d'Activité – Kapital States</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif; background:#fff; color:#1a1208; }
    .header { background:linear-gradient(135deg,#1a1208 0%,#3d2b0e 100%); padding:36px 48px 28px; }
    .logo { display:flex; align-items:center; gap:12px; margin-bottom:20px; }
    .logo-icon { width:40px; height:40px; background:#D4AF37; border-radius:10px; display:flex; align-items:center; justify-content:center; }
    .logo-text { font-size:20px; font-weight:700; color:#fff; letter-spacing:-0.02em; }
    .header-title { font-size:13px; color:rgba(212,175,55,0.8); letter-spacing:0.1em; text-transform:uppercase; font-weight:600; margin-bottom:6px; }
    .header-date { font-size:22px; font-weight:700; color:#fff; }
    .stats { display:flex; gap:16px; padding:28px 48px; background:#faf7f0; border-bottom:1px solid #e8dfc8; }
    .stat-card { flex:1; background:#fff; border:1px solid #e8dfc8; border-radius:12px; padding:16px; text-align:center; }
    .stat-val { font-size:28px; font-weight:700; color:#1a1208; line-height:1; margin-bottom:4px; }
    .stat-label { font-size:11px; color:#6b5c3e; text-transform:uppercase; letter-spacing:0.06em; font-weight:600; }
    .content { padding:28px 48px; }
    .section-title { font-size:14px; font-weight:700; color:#1a1208; letter-spacing:0.04em; text-transform:uppercase; margin-bottom:16px; padding-bottom:8px; border-bottom:2px solid #D4AF37; }
    table { width:100%; border-collapse:collapse; margin-bottom:32px; }
    thead tr { background:#faf7f0; }
    th { padding:10px 12px; text-align:left; font-size:11px; font-weight:700; color:#6b5c3e; text-transform:uppercase; letter-spacing:0.06em; border-bottom:2px solid #e8dfc8; }
    .footer { padding:24px 48px; background:#faf7f0; border-top:1px solid #e8dfc8; text-align:center; }
    .footer p { font-size:11px; color:#9b8a6e; line-height:1.6; }
    @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">
      <div class="logo-icon"><span style="color:#0a0a0a;font-weight:700;font-size:14px;">KS</span></div>
      <span class="logo-text">Kapital States</span>
    </div>
    <p class="header-title">Digest d'Activité ${periodLabel}</p>
    <p class="header-date">${dateLabel}</p>
    ${userEmail ? `<p style="color:rgba(255,255,255,0.45);font-size:13px;margin-top:6px;">${userEmail}</p>` : ''}
  </div>

  <div class="stats">
    <div class="stat-card">
      <div class="stat-val">${stats?.inquiries || 0}</div>
      <div class="stat-label">Demandes</div>
    </div>
    <div class="stat-card">
      <div class="stat-val">${stats?.viewings || 0}</div>
      <div class="stat-label">Visites</div>
    </div>
    <div class="stat-card">
      <div class="stat-val">${stats?.saved || 0}</div>
      <div class="stat-label">Sauvegardées</div>
    </div>
    <div class="stat-card">
      <div class="stat-val">${stats?.alerts || 0}</div>
      <div class="stat-label">Alertes</div>
    </div>
  </div>

  <div class="content">
    <p class="section-title">Détail des activités (${events?.length} événement${events?.length !== 1 ? 's' : ''})</p>
    <table>
      <thead>
        <tr>
          <th>Type</th>
          <th>Titre</th>
          <th>Détails</th>
          <th>Statut</th>
          <th>Date</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="5" style="padding:20px;text-align:center;color:#9b8a6e;">Aucune activité sur cette période</td></tr>'}
      </tbody>
    </table>
  </div>

  <div class="footer">
    <p>© ${now?.getFullYear()} Kapital States · Abidjan, Côte d'Ivoire · kapitalstates.com</p>
    <p style="margin-top:4px;">Ce rapport a été généré le ${now?.toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' })}</p>
  </div>
</body>
</html>`;
  return html;
};

// ─── Main Component ────────────────────────────────────────────────────────────

const ActivityDigestPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const printFrameRef = useRef(null);

  // Activity data
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ inquiries: 0, viewings: 0, saved: 0, alerts: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Digest preferences
  const [digestPrefs, setDigestPrefs] = useState({
    frequency: 'weekly',
    preferredHour: 8,
    preferredDayOfWeek: 1,
  });
  const [prefLoading, setPrefLoading] = useState(true);
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefSaved, setPrefSaved] = useState(false);

  // Send digest
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Preview period
  const [previewPeriod, setPreviewPeriod] = useState('weekly');

  // PDF export
  const [exporting, setExporting] = useState(false);

  // ─── Load activity ──────────────────────────────────────────────────────────

  const loadActivity = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const cutoff = new Date();
      if (previewPeriod === 'weekly') cutoff?.setDate(cutoff?.getDate() - 7);
      else if (previewPeriod === 'monthly') cutoff?.setMonth(cutoff?.getMonth() - 1);
      else cutoff?.setDate(cutoff?.getDate() - 1);

      const [inquiriesRes, appointmentsRes, wishlistRes, alertsRes] = await Promise.all([
        supabase?.from('property_inquiries')
          ?.select('*, property:properties(id,title,address)')
          ?.eq('client_id', user?.id)
          ?.gte('created_at', cutoff?.toISOString())
          ?.order('created_at', { ascending: false }),
        supabase?.from('appointments')
          ?.select('*, property:properties(id,title,address)')
          ?.eq('client_id', user?.id)
          ?.gte('created_at', cutoff?.toISOString())
          ?.order('scheduled_date', { ascending: false }),
        wishlistService?.getWishlist(user?.id, {}),
        getUserAlerts(user?.id),
      ]);

      const allEvents = [];

      (inquiriesRes?.data || [])?.forEach(item => {
        allEvents?.push({
          id: `inquiry-${item?.id}`,
          type: 'inquiry',
          date: item?.created_at,
          title: item?.property?.title || 'Propriété',
          subtitle: item?.message?.substring(0, 80) + (item?.message?.length > 80 ? '…' : ''),
          status: item?.status || 'new',
          propertyId: item?.property_id,
          address: item?.property?.address,
        });
      });

      (appointmentsRes?.data || [])?.forEach(item => {
        allEvents?.push({
          id: `viewing-${item?.id}`,
          type: 'viewing',
          date: item?.scheduled_date || item?.created_at,
          title: item?.property?.title || item?.appointment_type || 'Rendez-vous',
          subtitle: item?.notes || item?.property?.address || '',
          status: item?.status || 'scheduled',
          propertyId: item?.property_id,
          address: item?.property?.address,
        });
      });

      // Wishlist — filter by cutoff
      const wishlistItems = (wishlistRes?.data || [])?.filter(item => {
        const d = item?.created_at || item?.added_at;
        return d && new Date(d) >= cutoff;
      });
      wishlistItems?.forEach(item => {
        allEvents?.push({
          id: `saved-${item?.id || item?.property_id}`,
          type: 'saved',
          date: item?.created_at || item?.added_at,
          title: item?.property?.title || item?.title || 'Propriété sauvegardée',
          subtitle: item?.property?.address || item?.address || '',
          status: 'active',
          propertyId: item?.property_id,
          address: item?.property?.address || item?.address,
        });
      });

      // Alerts — filter by cutoff
      const alertItems = (alertsRes?.data || [])?.filter(item => {
        return item?.created_at && new Date(item?.created_at) >= cutoff;
      });
      alertItems?.forEach(item => {
        allEvents?.push({
          id: `alert-${item?.id}`,
          type: 'alert',
          date: item?.created_at,
          title: item?.name || 'Alerte propriété',
          subtitle: [
            item?.commune_names?.join(', '),
            item?.property_types?.join(', '),
          ]?.filter(Boolean)?.join(' · '),
          status: item?.is_active ? 'active' : 'inactive',
        });
      });

      allEvents?.sort((a, b) => new Date(b?.date) - new Date(a?.date));
      setEvents(allEvents);
      setStats({
        inquiries: (inquiriesRes?.data || [])?.length,
        viewings: (appointmentsRes?.data || [])?.length,
        saved: wishlistItems?.length,
        alerts: alertItems?.length,
      });
    } catch (err) {
      console.error(err);
      setError('Impossible de charger les données d\'activité.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, previewPeriod]);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  // ─── Load digest prefs ──────────────────────────────────────────────────────

  const loadPrefs = useCallback(async () => {
    if (!user?.id) { setPrefLoading(false); return; }
    setPrefLoading(true);
    const result = await getDigestPreferences(user?.id, user?.email);
    if (result?.success && result?.preferences) {
      setDigestPrefs(prev => ({
        ...prev,
        frequency: result?.preferences?.frequency === 'immediate' ? 'weekly' : (result?.preferences?.frequency || 'weekly'),
        preferredHour: result?.preferences?.preferredHour ?? 8,
        preferredDayOfWeek: result?.preferences?.preferredDayOfWeek ?? 1,
      }));
    }
    setPrefLoading(false);
  }, [user?.id, user?.email]);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  // ─── Save prefs ─────────────────────────────────────────────────────────────

  const handleSavePrefs = async () => {
    if (!user?.id) return;
    setPrefSaving(true);
    const result = await saveDigestPreferences(user?.id, user?.email, {
      frequency: digestPrefs?.frequency,
      preferredHour: digestPrefs?.preferredHour,
      preferredDayOfWeek: digestPrefs?.preferredDayOfWeek,
    });
    setPrefSaving(false);
    if (result?.success) {
      setPrefSaved(true);
      setTimeout(() => setPrefSaved(false), 3000);
    }
  };

  // ─── Send digest now ────────────────────────────────────────────────────────

  const handleSendNow = async () => {
    if (!user?.id || !user?.email) return;
    setSending(true);
    setSendResult(null);
    try {
      const { data: sessionData } = await supabase?.auth?.getSession();
      const accessToken = sessionData?.session?.access_token;

      const res = await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-activity-digest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          userId: user?.id,
          userEmail: user?.email,
          frequency: digestPrefs?.frequency,
          events,
          stats,
        }),
      });

      if (res?.ok) {
        setSendResult({ success: true, message: `Digest envoyé à ${user?.email}` });
      } else {
        const err = await res?.json()?.catch(() => ({}));
        setSendResult({ success: false, message: err?.error || 'Erreur lors de l\'envoi.' });
      }
    } catch (err) {
      setSendResult({ success: false, message: err?.message || 'Erreur réseau.' });
    } finally {
      setSending(false);
      setTimeout(() => setSendResult(null), 5000);
    }
  };

  // ─── PDF Export ─────────────────────────────────────────────────────────────

  const handleExportPDF = useCallback(() => {
    setExporting(true);
    try {
      const html = generatePDFContent(events, stats, previewPeriod, user?.email);
      const iframe = document?.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.top = '-9999px';
      iframe.style.left = '-9999px';
      iframe.style.width = '210mm';
      iframe.style.height = '297mm';
      document?.body?.appendChild(iframe);

      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      doc?.open();
      doc?.write(html);
      doc?.close();

      iframe?.contentWindow?.focus();
      setTimeout(() => {
        iframe?.contentWindow?.print();
        setTimeout(() => {
          document?.body?.removeChild(iframe);
          setExporting(false);
        }, 1000);
      }, 500);
    } catch (err) {
      console.error('PDF export error:', err);
      setExporting(false);
    }
  }, [events, stats, previewPeriod, user?.email]);

  // ─── Unauthenticated ────────────────────────────────────────────────────────

  if (!user) {
    return (
      <div className="min-h-screen bg-background font-system">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-16 h-16 bg-surface rounded-2xl flex items-center justify-center mb-4 shadow-ios-light">
            <Icon name="Lock" size={28} color="var(--color-brand-earth)" strokeWidth={1.5} />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-2">Connexion requise</h2>
          <p className="text-ios-body font-system text-text-secondary text-center mb-6 max-w-sm">
            Connectez-vous pour accéder à vos digests d'activité.
          </p>
          <Link to="/auth">
            <button className="px-6 py-3 bg-brand-earth text-white rounded-xl font-system font-body-medium text-ios-callout shadow-ios-light hover:opacity-90 transition-opacity">
              Se connecter
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const periodLabel = previewPeriod === 'weekly' ? '7 derniers jours' : previewPeriod === 'monthly' ? '30 derniers jours' : 'Aujourd\'hui';
  const totalEvents = events?.length;

  return (
    <div className="min-h-screen bg-background font-system">
      <Helmet>
        <title>Digest d'Activité — Kapital States</title>
        <meta name="description" content="Recevez automatiquement des résumés hebdomadaires ou mensuels de votre activité immobilière." />
      </Helmet>

      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-surface/70 transition-colors"
              aria-label="Retour"
            >
              <Icon name="ChevronLeft" size={20} color="var(--color-text-secondary)" strokeWidth={2} />
            </button>
            <div>
              <h1 className="text-ios-title1 font-display font-display-semibold text-brand-earth tracking-tight">
                Digest d'Activité
              </h1>
              <p className="text-ios-footnote font-system text-text-secondary mt-0.5">
                Résumés automatiques · export PDF · fréquence personnalisable
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Left: Settings Panel ── */}
          <div className="lg:col-span-1 space-y-5">

            {/* Frequency Settings */}
            <div className="bg-white rounded-2xl border border-border shadow-ios-light overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-border/40">
                <div className="w-9 h-9 rounded-xl bg-brand-earth/10 flex items-center justify-center">
                  <Icon name="Settings2" size={17} className="text-brand-earth" />
                </div>
                <div>
                  <p className="text-ios-callout font-body-semibold text-text-primary">Fréquence du digest</p>
                  <p className="text-ios-caption2 text-text-secondary">Envoi automatique par email</p>
                </div>
              </div>

              <div className="px-5 py-5 space-y-4">
                {prefLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <div className="w-4 h-4 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
                    <span className="text-ios-footnote text-text-secondary">Chargement…</span>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {FREQ_OPTIONS?.map(opt => (
                        <button
                          key={opt?.value}
                          onClick={() => setDigestPrefs(p => ({ ...p, frequency: opt?.value }))}
                          className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-all duration-150 text-left ${
                            digestPrefs?.frequency === opt?.value
                              ? 'bg-brand-earth/5 border-brand-earth ring-1 ring-brand-earth/20' :'bg-surface border-border hover:border-brand-earth/30'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            digestPrefs?.frequency === opt?.value ? 'bg-brand-earth/15' : 'bg-white border border-border'
                          }`}>
                            <Icon name={opt?.icon} size={15} className={digestPrefs?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-secondary'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-ios-callout font-body-semibold ${digestPrefs?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-primary'}`}>
                              {opt?.label}
                            </p>
                            <p className="text-ios-caption1 text-text-secondary">{opt?.desc}</p>
                          </div>
                          {digestPrefs?.frequency === opt?.value && (
                            <Icon name="CheckCircle2" size={16} className="text-brand-earth flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Delivery time */}
                    <div className="pt-2 border-t border-border/40">
                      <p className="text-ios-caption1 font-body-semibold text-text-secondary uppercase tracking-wide mb-3">Heure d'envoi</p>
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={14} className="text-text-secondary flex-shrink-0" />
                        <select
                          value={digestPrefs?.preferredHour}
                          onChange={e => setDigestPrefs(p => ({ ...p, preferredHour: parseInt(e?.target?.value) }))}
                          className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold outline-none bg-white"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>{String(i)?.padStart(2, '0')}:00</option>
                          ))}
                        </select>
                      </div>

                      {digestPrefs?.frequency === 'weekly' && (
                        <div className="flex items-center gap-2 mt-2">
                          <Icon name="CalendarDays" size={14} className="text-text-secondary flex-shrink-0" />
                          <select
                            value={digestPrefs?.preferredDayOfWeek}
                            onChange={e => setDigestPrefs(p => ({ ...p, preferredDayOfWeek: parseInt(e?.target?.value) }))}
                            className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/40 focus:border-brand-gold outline-none bg-white"
                          >
                            {DAYS?.map((d, i) => <option key={i} value={i}>{d}</option>)}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Save button */}
                    <div className="flex items-center justify-between pt-1">
                      {prefSaved && (
                        <span className="flex items-center gap-1.5 text-ios-caption1 text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
                          <Icon name="CheckCircle" size={12} />
                          Sauvegardé
                        </span>
                      )}
                      <button
                        onClick={handleSavePrefs}
                        disabled={prefSaving}
                        className="ml-auto flex items-center gap-2 px-4 py-2.5 bg-brand-earth text-white rounded-xl text-ios-footnote font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all shadow-ios-light"
                      >
                        {prefSaving ? (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Icon name="Save" size={13} color="white" />
                        )}
                        Sauvegarder
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Send Now */}
            <div className="bg-white rounded-2xl border border-border shadow-ios-light overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-border/40">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Icon name="Send" size={17} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-ios-callout font-body-semibold text-text-primary">Envoyer maintenant</p>
                  <p className="text-ios-caption2 text-text-secondary">Digest immédiat par email</p>
                </div>
              </div>
              <div className="px-5 py-5">
                <p className="text-ios-footnote text-text-secondary mb-4">
                  Envoyez un digest de votre activité récente à <span className="font-body-medium text-text-primary">{user?.email}</span> immédiatement.
                </p>

                {sendResult && (
                  <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border mb-4 text-ios-footnote ${
                    sendResult?.success
                      ? 'bg-green-50 border-green-100 text-green-700' :'bg-red-50 border-red-100 text-red-700'
                  }`}>
                    <Icon name={sendResult?.success ? 'CheckCircle' : 'AlertCircle'} size={14} />
                    {sendResult?.message}
                  </div>
                )}

                <button
                  onClick={handleSendNow}
                  disabled={sending || totalEvents === 0}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-ios-light"
                >
                  {sending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Envoi en cours…
                    </>
                  ) : (
                    <>
                      <Icon name="Mail" size={16} color="white" />
                      Envoyer le digest
                    </>
                  )}
                </button>
                {totalEvents === 0 && !loading && (
                  <p className="text-ios-caption2 text-text-secondary text-center mt-2">Aucune activité sur la période sélectionnée</p>
                )}
              </div>
            </div>

            {/* Quick links */}
            <div className="bg-white rounded-2xl border border-border shadow-ios-light px-5 py-4">
              <p className="text-ios-caption1 font-body-semibold text-text-secondary uppercase tracking-wide mb-3">Liens rapides</p>
              <div className="space-y-1">
                {[
                  { to: '/activity-history', icon: 'ClipboardList', label: 'Historique complet' },
                  { to: '/notification-preferences', icon: 'Bell', label: 'Préférences notifications' },
                  { to: '/user-profile', icon: 'UserCircle', label: 'Mon profil' },
                ]?.map(link => (
                  <Link
                    key={link?.to}
                    to={link?.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-surface transition-colors group"
                  >
                    <Icon name={link?.icon} size={15} className="text-text-secondary group-hover:text-brand-earth transition-colors" />
                    <span className="text-ios-footnote text-text-secondary group-hover:text-text-primary transition-colors">{link?.label}</span>
                    <Icon name="ChevronRight" size={13} className="text-text-secondary ml-auto" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right: Preview Panel ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Preview controls */}
            <div className="bg-white rounded-2xl border border-border shadow-ios-light p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <p className="text-ios-callout font-body-semibold text-text-primary">Aperçu du digest</p>
                  <p className="text-ios-caption1 text-text-secondary mt-0.5">
                    {totalEvents} événement{totalEvents !== 1 ? 's' : ''} · {periodLabel}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Period selector */}
                  <div className="flex items-center bg-surface rounded-xl border border-border p-1 gap-1">
                    {[
                      { v: 'daily', l: 'Jour' },
                      { v: 'weekly', l: 'Semaine' },
                      { v: 'monthly', l: 'Mois' },
                    ]?.map(p => (
                      <button
                        key={p?.v}
                        onClick={() => setPreviewPeriod(p?.v)}
                        className={`px-3 py-1.5 rounded-lg text-ios-caption1 font-body-medium transition-all duration-150 ${
                          previewPeriod === p?.v
                            ? 'bg-white text-brand-earth shadow-ios-light border border-border/60'
                            : 'text-text-secondary hover:text-text-primary'
                        }`}
                      >
                        {p?.l}
                      </button>
                    ))}
                  </div>

                  {/* PDF Export */}
                  <button
                    onClick={handleExportPDF}
                    disabled={exporting || totalEvents === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-earth text-white rounded-xl text-ios-footnote font-body-medium hover:bg-brand-earth/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-ios-light"
                  >
                    {exporting ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Icon name="FileDown" size={14} color="white" />
                    )}
                    Export PDF
                  </button>
                </div>
              </div>
            </div>

            {/* Stats summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { key: 'inquiries', label: 'Demandes',     icon: 'MessageSquare', value: stats?.inquiries, color: 'text-blue-600',   bg: 'bg-blue-50'   },
                { key: 'viewings',  label: 'Visites',      icon: 'Calendar',      value: stats?.viewings,  color: 'text-violet-600', bg: 'bg-violet-50' },
                { key: 'saved',     label: 'Sauvegardées', icon: 'Heart',         value: stats?.saved,     color: 'text-rose-600',   bg: 'bg-rose-50'   },
                { key: 'alerts',    label: 'Alertes',      icon: 'Bell',          value: stats?.alerts,    color: 'text-amber-600',  bg: 'bg-amber-50'  },
              ]?.map(stat => (
                <div key={stat?.key} className="bg-white border border-border rounded-2xl p-4 shadow-ios-light">
                  <div className={`w-9 h-9 ${stat?.bg} rounded-xl flex items-center justify-center mb-3`}>
                    <Icon name={stat?.icon} size={17} className={stat?.color} strokeWidth={2} />
                  </div>
                  <div className="text-ios-title2 font-display font-display-semibold text-brand-earth leading-none mb-1">
                    {loading ? '—' : stat?.value}
                  </div>
                  <div className="text-ios-caption1 font-system text-text-secondary">{stat?.label}</div>
                </div>
              ))}
            </div>

            {/* Event list preview */}
            <div className="bg-white rounded-2xl border border-border shadow-ios-light overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
                <p className="text-ios-callout font-body-semibold text-text-primary">
                  Activités incluses dans le digest
                </p>
                <span className="text-ios-caption1 text-text-secondary bg-surface px-2.5 py-1 rounded-full border border-border">
                  {totalEvents} événement{totalEvents !== 1 ? 's' : ''}
                </span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-7 h-7 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
                </div>
              ) : error ? (
                <div className="px-5 py-10 text-center">
                  <Icon name="AlertCircle" size={28} className="text-red-400 mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-ios-footnote text-red-600">{error}</p>
                  <button onClick={loadActivity} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-xl text-ios-caption1 font-body-medium hover:opacity-90 transition-opacity">
                    Réessayer
                  </button>
                </div>
              ) : events?.length === 0 ? (
                <div className="px-5 py-14 text-center">
                  <div className="w-14 h-14 bg-surface rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon name="Inbox" size={26} color="var(--color-text-secondary)" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-ios-callout font-body-semibold text-brand-earth mb-1">Aucune activité</h3>
                  <p className="text-ios-footnote text-text-secondary max-w-xs mx-auto">
                    Aucune activité sur {periodLabel?.toLowerCase()}. Essayez une période plus longue.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border/40">
                  {events?.map(event => {
                    const cfg = EVENT_CONFIG?.[event?.type];
                    return (
                      <div key={event?.id} className="flex items-start gap-4 px-5 py-4 hover:bg-surface/40 transition-colors">
                        <div className={`w-9 h-9 ${cfg?.bg} rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Icon name={cfg?.icon} size={16} className={cfg?.color} strokeWidth={2} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className={`text-ios-caption2 font-body-medium px-2 py-0.5 rounded-full border ${cfg?.bg} ${cfg?.color} ${cfg?.border}`}>
                              {cfg?.label}
                            </span>
                            <span className="text-ios-caption2 text-text-secondary bg-surface px-2 py-0.5 rounded-full border border-border">
                              {STATUS_LABELS?.[event?.status] || event?.status}
                            </span>
                          </div>
                          <p className="text-ios-footnote font-body-semibold text-text-primary truncate">{event?.title}</p>
                          {event?.subtitle && (
                            <p className="text-ios-caption1 text-text-secondary mt-0.5 line-clamp-1">{event?.subtitle}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                          <span className="text-ios-caption2 text-text-secondary whitespace-nowrap">{formatDate(event?.date)}</span>
                          {event?.propertyId && (
                            <Link
                              to={`/property/${event?.propertyId}`}
                              className="text-ios-caption2 font-body-medium text-brand-earth hover:underline flex items-center gap-0.5"
                            >
                              Voir <Icon name="ArrowRight" size={11} strokeWidth={2} />
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Email preview note */}
            <div className="bg-brand-earth/5 border border-brand-earth/15 rounded-2xl px-5 py-4 flex items-start gap-3">
              <Icon name="Info" size={16} className="text-brand-earth flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="text-ios-footnote font-body-semibold text-brand-earth mb-1">Comment fonctionne le digest automatique ?</p>
                <p className="text-ios-caption1 text-text-secondary leading-relaxed">
                  Selon la fréquence choisie, un email récapitulatif sera envoyé automatiquement à <span className="font-body-medium">{user?.email}</span> avec toutes vos activités (demandes, visites, propriétés sauvegardées, alertes déclenchées). Vous pouvez aussi exporter ce rapport en PDF à tout moment.
                </p>
              </div>
            </div>

          </div>
        </div>

        <div className="h-12" />
      </main>
    </div>
  );
};

export default ActivityDigestPage;
